/**
 * Shared active-client filter for ALL dashboard hooks.
 *
 * The dashboard is a live client-facing surface. Data from clients with
 * `status != 'Active'` (churned workspaces like Loop, Yotpo, Bayezon, and
 * contract_completed clients like PrettyDamnQuick) MUST NOT leak anywhere —
 * not on the Campaigns row, not on the Gantt calendar, not in Segment
 * Performance, not in Offer Matchups, not in the Winning Angle hero, not
 * in the Funnel Profile pies.
 *
 * This module exposes two helpers used by every dashboard hook:
 *
 *   - useActiveClientIds(): id/client_id Set — for tables keyed by UUID
 *     or legacy text ID (campaigns.client_id, clients.id, clients.client_id).
 *
 *   - useActiveClientNames(): canonical name Set — for tables keyed by
 *     client name (masterinbox_replies.workspace_name, obsync_dynamic_
 *     reports.workspace_name, meetings_booked.client, deals_won.client).
 *
 * Both exclude internal test clients (`Global Default Keys`, `Outboundbuddy`)
 * the same way useConversionFunnel already does, so admin aggregates
 * ("All Clients") match across old and new surfaces.
 *
 * Usage pattern — prefer the Set lookup over a .in() filter when the
 * surface already has a large payload (e.g. a CTE output in a custom RPC).
 * For simple `.in()` calls use `Array.from(set)`.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

/**
 * Sales-demo / sandbox clients (Flowmail — see project memory). Their data
 * is fake/inflated and MUST NOT be summed into admin "All Clients"
 * aggregates, but their own per-client view (demo login) must keep working.
 *
 * So these clients stay in the active-client list returned below (per-client
 * resolution needs them), and each ALL-CLIENTS aggregation branch additionally
 * filters them out via the helpers here. Never use these to filter a
 * per-client query — that would break the demo's own dashboard.
 */
export const DEMO_CLIENT_IDS = new Set<string>([
  'de300000-0000-4000-8000-000000000001', // Flowmail clients.id
  '990001', // Flowmail clients.client_id
])
export const DEMO_CLIENT_NAMES = new Set<string>(['Flowmail'])

/** True if this client/workspace name is a sales-demo/sandbox client. */
export function isDemoClientName(name: string | null | undefined): boolean {
  return name != null && DEMO_CLIENT_NAMES.has(String(name).trim())
}

/** True if this id or numeric client_id belongs to a sales-demo/sandbox client. */
export function isDemoClientId(id: string | number | null | undefined): boolean {
  return id != null && DEMO_CLIENT_IDS.has(String(id))
}

export interface ActiveClient {
  /** UUID or legacy numeric text — whatever sits in `clients.id`. */
  id: string
  /** Legacy text ID (often numeric) — whatever sits in `clients.client_id`. */
  client_id: string | null
  /** Canonical display name — matches `masterinbox_replies.workspace_name`,
   *  `meetings_booked.client`, `deals_won.client`. */
  name: string
}

/**
 * Internal fetch — Active, non-test clients only. All three excluded
 * names (`%default%`, `%outboundbuddy%`) mirror useConversionFunnel so
 * counts line up across the app.
 */
async function fetchActiveClients(): Promise<ActiveClient[]> {
  const { data, error } = await supabase
    .from('clients')
    .select('id, client_id, name')
    .eq('status', 'Active')
    .not('name', 'ilike', '%default%')
    .not('name', 'ilike', '%outboundbuddy%')
  if (error) throw error
  return ((data || []) as any[]).map((c) => ({
    id: String(c.id),
    client_id: c.client_id != null ? String(c.client_id) : null,
    name: String(c.name ?? ''),
  }))
}

/**
 * Returns a Set containing BOTH `clients.id` AND `clients.client_id` for
 * every Active non-internal client. Use for membership checks against
 * `campaigns.client_id` etc.
 */
export function useActiveClientIds(): {
  set: Set<string>
  loading: boolean
  clients: ActiveClient[]
} {
  // Cache arrays — never `Set` instances — because the React-Query cache is
  // persisted to localStorage by `PersistQueryClientProvider`. Sets serialize
  // to `{}` via JSON.stringify and lose their prototype on rehydration,
  // causing `l.has is not a function` crashes downstream. Reconstruct the
  // Set at read time via useMemo.
  const { data, isLoading } = useQuery({
    // -v2 bump (Jamie 2026-05-10) — invalidate any pre-filter cached lists
    queryKey: ['active-client-ids-v2'],
    queryFn: async () => {
      const clients = await fetchActiveClients()
      const ids: string[] = []
      for (const c of clients) {
        ids.push(c.id)
        if (c.client_id) ids.push(c.client_id)
      }
      return { ids, clients }
    },
    staleTime: 60_000,
  })
  const set = useMemo(() => new Set<string>(data?.ids ?? []), [data?.ids])
  return {
    set,
    clients: data?.clients ?? [],
    loading: isLoading,
  }
}

/**
 * Returns a Set of canonical client NAMES for every Active non-internal
 * client. Use for membership checks against `workspace_name`, `client`,
 * etc. text columns.
 */
export function useActiveClientNames(): {
  set: Set<string>
  loading: boolean
} {
  // See note in useActiveClientIds — cache arrays, not Sets, so the persisted
  // localStorage cache rehydrates correctly.
  const { data, isLoading } = useQuery({
    queryKey: ['active-client-names-v2'],
    queryFn: async () => {
      const clients = await fetchActiveClients()
      return clients.map((c) => c.name).filter(Boolean)
    },
    staleTime: 60_000,
  })
  const set = useMemo(() => new Set<string>(data ?? []), [data])
  return { set, loading: isLoading }
}

/**
 * Non-hook async version — for use inside queryFn bodies that already
 * own their own react-query lifecycle and don't want to compose hooks.
 * Returns all three shapes in one shot.
 *
 * D-051 (Sabeeh, 2026-05-06): request-level memoisation. Before this
 * change every consumer (useConversionFunnel current+prior,
 * useMasterinboxMeetings, useFirstPositiveCount current+prior,
 * useOfferAngleAnalysis, useFunnelProfile, NewPipelinePage)
 * triggered a fresh `clients` SELECT each time their queryFn ran —
 * 13 identical round-trips on cold dashboard load (~8.5s total).
 *
 * Memoise the in-flight promise so concurrent callers share one
 * network call. Re-fetch after 60s (matches the staleTime of the
 * useActiveClientIds/Names hooks above so the two paths agree).
 */
type ActiveClientsResult = {
  idSet: Set<string>
  nameSet: Set<string>
  clients: ActiveClient[]
}
let _activeClientsCache: { promise: Promise<ActiveClientsResult>; expiresAt: number } | null = null
const ACTIVE_CLIENTS_TTL_MS = 60_000

export async function loadActiveClients(): Promise<ActiveClientsResult> {
  const now = Date.now()
  if (_activeClientsCache && now < _activeClientsCache.expiresAt) {
    return _activeClientsCache.promise
  }
  const promise = (async () => {
    const clients = await fetchActiveClients()
    const idSet = new Set<string>()
    const nameSet = new Set<string>()
    for (const c of clients) {
      idSet.add(c.id)
      if (c.client_id) idSet.add(c.client_id)
      if (c.name) nameSet.add(c.name)
    }
    return { idSet, nameSet, clients }
  })()
  _activeClientsCache = { promise, expiresAt: now + ACTIVE_CLIENTS_TTL_MS }
  // If the underlying fetch fails, drop the cache so the next caller
  // retries instead of re-using a rejected promise.
  promise.catch(() => {
    if (_activeClientsCache?.promise === promise) _activeClientsCache = null
  })
  return promise
}
